import axios from "axios";
import { API_BASE_URL } from "./contests";

interface CacheEntry {
    value: any;
    createdAt: number;  // Local TTL tracking
}

const apiCache = new Map<string, CacheEntry>();
let lastKnownTimestamp: number | null = null;
let latestAcquired: { timestamp: number; expiresAt: number } | null = null;
const TIMESTAMP_VALIDITY_WINDOW_MS = 60000; // 1 minute

export const getCacheTimestamp = async (): Promise<number> => {
    if (latestAcquired && Date.now() < latestAcquired.expiresAt) {
        return latestAcquired.timestamp;
    }

    try {
        const response = await axios.get(`${API_BASE_URL}/cache-timestamp`);
        const newTimestamp = response.data.timestamp;
        latestAcquired = {
            timestamp: newTimestamp,
            expiresAt: Date.now() + TIMESTAMP_VALIDITY_WINDOW_MS,
        };
        return newTimestamp;
    } catch (error) {
        console.error("Cache timestamp check failed, using fallback:", error);
        return Date.now();
    }
};

export function Cacheable(prefix: string = '', ttl?: number) {
    return function <T extends (...args: any[]) => Promise<any>>(fn: T): T {
        return (async function (...args: any[]) {
            const key = `${prefix}${fn.name}_${JSON.stringify(args)}`;
            const currentTimestamp = await getCacheTimestamp();

            // Global invalidation (server timestamp changed)
            if (lastKnownTimestamp !== null && currentTimestamp !== lastKnownTimestamp) {
                apiCache.clear();
            }
            lastKnownTimestamp = currentTimestamp;

            // Check local cache (with optional TTL)
            const cached = apiCache.get(key);
            if (cached) {
                const isExpired = ttl !== undefined && (Date.now() - cached.createdAt) > ttl;
                if (!isExpired) {
                    return cached.value;
                }
                apiCache.delete(key); // Remove expired entry
            }

            // Fetch fresh data
            const result = await fn(...args);
            apiCache.set(key, {
                value: result,
                createdAt: Date.now(), // Track creation for TTL
            });
            return result;
        } as T);
    };
}

export function clearApiCache() {
    apiCache.clear();
    lastKnownTimestamp = null;
    latestAcquired = null;
}